/**
 * country-notice-mab.js
 *
 * Single responsibility: fetch the Shopify browsing-context endpoint, decide whether the
 * visitor is outside Switzerland and Liechtenstein, and only then swap in the items marked
 * data-requires-outside-ch, hiding any marked data-replaced-outside-ch that they stand in for.
 *
 * Fail-closed by design. Every failure path — a rejected fetch, a non-OK status, malformed
 * JSON, a missing or non-two-letter handle, a handle of CH or LI — leaves the item hidden,
 * which is the site's behaviour today. Only an unambiguous positive detection reveals it.
 *
 * Loaded as a module, so two references to the same URL still execute once. The announcement
 * band loads it only when it renders a gated item; the product assurances section loads it
 * whenever the row renders, because the two surfaces read different metaobjects and neither can
 * answer for the other. The guard below is what makes that cheap: with no gated item in the
 * document it returns before fetching.
 */

const ENDPOINT = '/browsing_context_suggestions.json?country[enabled]=true';

/** Two-letter handles that must NOT reveal the item. */
const EXCLUDED = new Set(['CH', 'LI']);

class CountryNotice {
  #items;
  #replaced;

  constructor() {
    this.#items = Array.from(document.querySelectorAll('[data-requires-outside-ch]'));
    // Items the gated ones stand in for. On the product page the delivery time is replaced rather
    // than joined: "dispatched in 1-2 working days" is untrue for a visitor the store cannot ship
    // to, and a page carrying both would contradict itself.
    this.#replaced = Array.from(document.querySelectorAll('[data-replaced-outside-ch]'));
  }

  async run() {
    if (this.#items.length === 0) return;

    if (!(await this.#detect())) return;

    this.#reveal();
  }

  /** True only for a present, two-letter country that is neither CH nor LI. False on every
   *  other outcome — a rejected fetch, a non-OK status, malformed JSON, a missing or malformed
   *  handle. The handle itself is never needed: nothing rendered depends on which country it is. */
  async #detect() {
    try {
      const res = await fetch(ENDPOINT, { credentials: 'same-origin' });
      if (!res.ok) return false;

      const data = await res.json();
      const handle = data?.detected_values?.country?.handle;

      // A falsy value, a non-string, or anything that is not exactly two letters is treated
      // as no answer — the item stays hidden.
      if (!handle || typeof handle !== 'string' || handle.length !== 2) return false;
      if (EXCLUDED.has(handle)) return false;

      return true;
    } catch {
      return false;
    }
  }

  /** Swap the gated items in and whatever they stand in for out. */
  #reveal() {
    for (const item of this.#replaced) {
      item.setAttribute('hidden', '');
    }

    for (const item of this.#items) {
      item.removeAttribute('hidden');
    }
  }
}

new CountryNotice().run();
